import { Text } from "@repo/skapa";
import { Button } from "@repo/skapa";
import { t } from "@repo/translation";
import { useCustomer, useToast } from "@repo/ui";
import { ModalSheet } from "@repo/ui";
import { trackAnalytics } from "@repo/utils";
import { useState } from "react";

import { TagsGroup } from "./TagsGroup";
import { saveTags, useTags } from "./tags";
import { filterTagsByCustomerType } from "./utils";

interface SelectTagsModalProps {
  isOpen: boolean;
  closeModal: () => void;
  selected: string[];
  opportunityId?: number;
  onAsyncUpdate?: (tags: string[]) => Promise<void>;
}

export const SelectTagsModal = ({
  isOpen,
  closeModal,
  selected,
  opportunityId,
  onAsyncUpdate,
}: SelectTagsModalProps) => {
  const [localSelected, setLocalSelected] = useState<string[]>(selected);
  const [loading, setLoading] = useState<boolean>(false);
  const { customer } = useCustomer(opportunityId);
  const { updateToast } = useToast();
  const { data: tags } = useTags();
  const filteredTags = filterTagsByCustomerType(
    tags ?? [],
    customer?.customerType
  );

  // filter out system tags
  const payloadData = filteredTags
    .filter(
      (tag) =>
        tag.isSystemGenerated !== true &&
        localSelected.find((selected) => tag.name === selected)
    )
    .flatMap((tag) => tag.name);

  return (
    <ModalSheet
      visible={isOpen}
      onClose={closeModal}
      title={t("label.tags")}
      onBack={() => {
        closeModal();
        trackAnalytics("back_from_tag");
      }}
      footer={
        <Button
          type="primary"
          onClick={async () => {
            setLoading(true);
            if (onAsyncUpdate) {
              await onAsyncUpdate(payloadData);
            }
            if (opportunityId != null) {
              saveTags(payloadData, opportunityId)
                .then(() => {
                  updateToast({
                    id: opportunityId,
                    title: t("prompt.tags_saved"),
                  });
                })
                .catch((err) => {
                  updateToast({
                    id: opportunityId,
                    title: t("prompt.tag_save_failed"),
                    message: err,
                    type: "error",
                  });
                })
                .finally(() => {
                  setLoading(false);
                  closeModal();
                  trackAnalytics("save_tags");
                });
            } else {
              setLoading(false);
              closeModal();
            }
          }}
          loading={loading}
          text={t("action.save")}
        />
      }
    >
      {!filteredTags?.length ? (
        <Text>{t("label.no_tags_available")}</Text>
      ) : (
        <TagsGroup
          localState={localSelected ?? []}
          onChange={(checked, item) => {
            setLocalSelected(
              checked
                ? [...(localSelected ?? []), item.name]
                : (localSelected?.filter((el) => el !== item.name) ?? [])
            );
            trackAnalytics("select_tag");
          }}
          tags={filteredTags}
        />
      )}
    </ModalSheet>
  );
};
